import { useMutation, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query'
import axios from 'axios'

import { CacheKeys } from '../lib/cache'
import { toCamelCase } from '../lib/typeHelper'

export interface CabCarrierContactInfo {
  name: string | null
  title: string | null
  email: string | null
  phone: string | null
  address: string | null
  city: string | null
  state: string | null
  zipCode: string | null
}

export interface CabCarrierAuthority {
  commonAuthority: string | null
  contractAuthority: string | null
  brokerAuthority: string | null
  authorityStatus: string | null
  authorityGrantDate: string | null
}

export interface CabCarrierInsuranceData {
  bipdRequired: boolean | null
  bipdOnFile: number | null
  cargoRequired: boolean | null
  cargoOnFile: number | null
  bondRequired: boolean | null
  bondOnFile: number | null
  insuranceCarrier: string | null
  policyNumber: string | null
  effectiveDate: string | null
  expirationDate: string | null
}

export interface CabCarrier {
  dotNumber: string
  legalName: string | null
  dbaName: string | null
  entityType: string | null
  operatingStatus: string | null
  powerUnits: number | null
  drivers: number | null
  mcs150Mileage: number | null
  cargoCarried: string[] | null
  operationClassification: string[] | null
  yearsInBusiness: number | null
  yearsSinceReinstatement: number | null
  contact: CabCarrierContactInfo | null
  authority: CabCarrierAuthority | null
  insurance: CabCarrierInsuranceData | null
}

export interface CabLookupResponse {
  carrier: CabCarrier | null
  insurance: Record<string, unknown> | null
  insuranceHistory: Record<string, unknown> | null
  contacts: CabCarrierContactInfo | null
  authorityHistory: Record<string, unknown>[] | null
}

export interface CabVehicleNhtsa {
  make?: string
  model?: string
  year?: number
  vehicleClass?: string
  weightClass?: string
}

export interface CabVehicle {
  vin: string | null
  make: string | null
  model: string | null
  year: number | null
  vehicleType: string | null
  nhtsa: CabVehicleNhtsa | null
}

export interface CabCrash {
  id: string
  date: string | null
  time: string | null
  fatalities: number
  injuries: number
  towAway: boolean
  vehiclesInCrash: number
  location: string | null
  city: string | null
  state: string | null
}

export interface CabSevereViolation {
  id?: string
  code?: string
  description?: string | null
  basicCategory?: string | null
  oos?: boolean
  inspectionDate?: string | null
  state?: string | null
  [key: string]: unknown
}

function fetchCabLookup(dotNumber: string): Promise<CabLookupResponse> {
  return axios
    .get(`/api/v1/insure/internal/cab/${dotNumber}/`)
    .then((response) => toCamelCase(response.data) as CabLookupResponse)
}

function fetchCabVehicles(dotNumber: string): Promise<CabVehicle[]> {
  return axios
    .get(`/api/v1/insure/internal/cab/${dotNumber}/vehicles/`)
    .then((response) => toCamelCase(response.data) as CabVehicle[])
}

function fetchCabCrashes(dotNumber: string): Promise<CabCrash[]> {
  return axios
    .get(`/api/v1/insure/internal/cab/${dotNumber}/crashes/`)
    .then((response) => toCamelCase(response.data) as CabCrash[])
}

function fetchCabSevereViolations(dotNumber: string): Promise<CabSevereViolation[]> {
  return axios
    .get(`/api/v1/insure/internal/cab/${dotNumber}/severe-violations/`)
    .then((response) => toCamelCase(response.data) as CabSevereViolation[])
}

export function useCabLookup(dotNumber: string | undefined): UseQueryResult<CabLookupResponse> {
  return useQuery({
    queryKey: [CacheKeys.CAB_LOOKUP, dotNumber],
    queryFn: () => fetchCabLookup(dotNumber!),
    enabled: !!dotNumber,
  })
}

export function useCabVehicles(dotNumber: string | undefined): UseQueryResult<CabVehicle[]> {
  return useQuery({
    queryKey: [CacheKeys.CAB_VEHICLES, dotNumber],
    queryFn: () => fetchCabVehicles(dotNumber!),
    enabled: !!dotNumber,
  })
}

export function useCabCrashes(dotNumber: string | undefined): UseQueryResult<CabCrash[]> {
  return useQuery({
    queryKey: [CacheKeys.CAB_CRASHES, dotNumber],
    queryFn: () => fetchCabCrashes(dotNumber!),
    enabled: !!dotNumber,
  })
}

export function useCabSevereViolations(dotNumber: string | undefined): UseQueryResult<CabSevereViolation[]> {
  return useQuery({
    queryKey: [CacheKeys.CAB_SEVERE_VIOLATIONS, dotNumber],
    queryFn: () => fetchCabSevereViolations(dotNumber!),
    enabled: !!dotNumber,
  })
}

export interface ImportLeadResult {
  leadId: string
  created: boolean
}

export function useCabLead(dotNumber: string | undefined): UseQueryResult<{ leadId: string }> {
  return useQuery({
    queryKey: [CacheKeys.CAB_LEAD, dotNumber],
    queryFn: async () => {
      const res = await axios.get(`/api/v1/insure/internal/cab/${dotNumber}/lead/`)
      return toCamelCase(res.data) as { leadId: string }
    },
    enabled: !!dotNumber,
    retry: false,
  })
}

export function useImportLeadFromCab() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (dotNumber: string): Promise<ImportLeadResult> => {
      const res = await axios.post(`/api/v1/insure/internal/cab/${dotNumber}/import-lead/`)
      return toCamelCase(res.data) as ImportLeadResult
    },
    onSuccess: (data, dotNumber) => {
      queryClient.setQueryData([CacheKeys.CAB_LEAD, dotNumber], { leadId: data.leadId })
    },
  })
}
